<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCrewStore } from "@/stores/crewStore";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";


const props = defineProps<{
  showEditModal: boolean,
  name: string,
  crewlist: { uuid: string; listName: string }
}>();


const emit = defineEmits<{
  'on-close': [void],
  'on-edit-click': [{ id: string, name: string }]
}>();

const { t } = useI18n();

// Add: store + saving/error state
const crewStore = useCrewStore();
const isSaving = ref(false);
const editError = ref<string | null>(null);

// Create local editable copy of the listName
const editedName = ref(props.name ?? '');

watch(
  () => props.name,
  (newVal) => {
    editedName.value = newVal ?? '';
  },
  { immediate: true }
);

async function handleUpdate() {
  editError.value = null;
  const name = editedName.value.trim();
  const id = props.crewlist?.uuid || '';

  if (!id || !name) {
    editError.value = t('crewlistEditModal.nameRequired') || 'Name is required';
    return;
  }

  try {
    isSaving.value = true;
    const response = await crewStore.updateCrewList(id, name);
    if (response?.data) {
      // if the API returned the updated list, update store directly
      if (crewStore.replaceList) {
        crewStore.replaceList(response.data);
      }
    } else {
      // otherwise refresh all lists
      await crewStore.fetchAllCrewLists();
    }
    // close on success
    isSaving.value = false;
    emit('on-close');
  } catch (e: any) {
    // show error, keep modal open and input intact
    editError.value = e?.response?.data?.message || e?.message || (t('crewlistEditModal.updateFailed') as string) || 'Failed to update list. Please try again.';
    isSaving.value = false;
  }
}

// Body scroll lock if popup displayed
watch(() => props.showEditModal, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
  }
});


</script>


<template>
  <CustomModal  :show="showEditModal" @close="$emit('on-close')" classes="w-1/6" :mobile-fullscreen="false" :custom-index="9999" extra-classes="h-fit" drawerHeight="fit-content">
    <div class="p-5 flex flex-col modalDelete sm:min-w-[346px] sm:min-h-[243px]">
      <div class="cursor-pointer self-end hidden sm:block" type="default" @click="$emit('on-close')">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
      </div>
      <div class="py-3.5 flex flex-col gap-8">
        <div class="responsive-title-6 font-bold">{{ t('crewlistEditModal.editList') }}</div>
        <VTextField
            v-model="editedName"
            :label="t('crewlistEditModal.editListName')"
            variant="underlined"
            :disabled="isSaving"
        />

        <div v-if="editError" class="text-notifications-red responsive-body-xsmall -mt-4">
          {{ editError }}
        </div>

        <div class="flex gap-4 justify-space-between">
          <CustomButton
              type="black"
              class="self-start"
              :rounded="true"
              :outlined="false"
              :disabled="isSaving || !editedName?.trim()"
              @click="handleUpdate"
          >
            <div class="flex items-center gap-2">
              <CustomSpinner v-if="isSaving" :size="16" color="white" />
              <span>{{ t('crewlistEditModal.updateButton') }}</span>
            </div>
          </CustomButton>
          <CustomButton
              :outlined="true"
              :rounded="true"
              class="hover:bg-black hover:text-white hover:border-black transition-all"
              :disabled="isSaving"
              @click="$emit('on-close')">
            {{ t('crewlistEditModal.cancelButton') }}
          </CustomButton>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style scoped>
</style>
